import { authorizedFetch } from '../../helpers/authorizedFetch'
import { formatDate } from '../../helpers/date'
import trans from '../../helpers/trans'
import { generateApiUrl } from '../../helpers/url'
import getIncluded from '../../japi/getIncluded'
import isResourceCollectionDocument from '../../japi/guards/isResourceCollectionDocument'
import isResourceDocument from '../../japi/guards/isResourceDocument'
import { JapiDocument } from '../../japi/types/Document'
import { DateFormat } from '../../types/dateFormatTypes'
import { EmploymentTypeSlug } from '../EmploymentType/EmploymentType'
import { FetchCollectionResult, FetchResult, FetchResultType } from '../FetchResult'
import { NotificationCenterHeaderName, NotificationCenterLog } from '../NotificationCenter/NotificationCenter'
import { Period } from '../Period/Period'
import { UserResource } from '../User/User'
import { transformToUser } from '../User/UserTransformers'
import { Absence, AbsenceFormData, AbsenceResource } from './Absence'
import { transformToAbsence } from './AbsenceTransformers'

export interface GetAbsenceCallApiCallProps {
    period: Period;
    departments?: string[];
    employeeSearch?: string;
    employmentTypeSlugs?: EmploymentTypeSlug[];
}

export const getAbsencesApiCall = async (filters: GetAbsenceCallApiCallProps): Promise<FetchCollectionResult<Absence[], string>> => {
    try {
        const { period, departments } = filters
        const includes = ['user']

        const url = generateApiUrl({
            endpoint: '/absences',
            queryParams: {
                include: includes.join(','),
                ...((departments && departments.length) && { 'departments[]': departments.join('departments[]=') }),
                startDate: formatDate(period.start, DateFormat.apiDateTime),
                endDate: formatDate(period.end, DateFormat.apiDateTime),
            },
        })

        const response = await authorizedFetch(url)

        const doc: JapiDocument = await response.json()

        if (! isResourceCollectionDocument<AbsenceResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const absences = doc.data.map(absenceResource => {
            const userResource = getIncluded(doc, absenceResource, 'user') as UserResource
            const user = transformToUser(userResource)

            return transformToAbsence(
                absenceResource,
                user,
            )
        })

        return {
            amountOfPages: 1,
            status: response.status,
            type: FetchResultType.Success,
            data: absences,
        }
    } catch (error) {
        console.error('[getAbsencesApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const postAbsenceApiCall = async (
    data: AbsenceFormData,
    userId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<Absence, string>> => {
    try {
        const includes = ['user']

        const url = generateApiUrl({
            endpoint: '/absences',
            queryParams: {
                include: includes.join(','),
            },
        })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify({
                data: {
                    type: 'absences',
                    attributes: {
                        start: formatDate(data.start, DateFormat.apiDateTime),
                    },
                    relationships: {
                        user: {
                            data: {
                                type: 'users',
                                id: userId,
                            },
                        },
                    },
                },
            }),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<AbsenceResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        const userResource = getIncluded(doc, doc.data, 'user') as UserResource
        const user = transformToUser(userResource)
        const absence = transformToAbsence(doc.data, user)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: absence,
        }
    } catch (error) {
        console.error('[postAbsenceApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const patchAbsenceApiCall = async (
    absenceId: string,
    data: AbsenceFormData,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<Absence, string>> => {
    try {
        const includes = ['user']

        const url = generateApiUrl({
            endpoint: `/absences/${absenceId}`,
            queryParams: {
                include: includes.join(','),
            },
        })

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            body: JSON.stringify({
                data: {
                    type: 'absences',
                    id: absenceId,
                    attributes: {
                        start: formatDate(data.start, DateFormat.apiDateTime),
                        end: data.end ? formatDate(data.end, DateFormat.apiDateTime) : data.end,
                    },
                },
            }),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<AbsenceResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        const userResource = getIncluded(doc, doc.data, 'user') as UserResource
        const user = transformToUser(userResource)
        const absence = transformToAbsence(doc.data, user)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: absence,
        }
    } catch (error) {
        console.error('[patchAbsenceApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const deleteAbsenceApiCall = async (
    absenceId: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<any, string>> => {
    try {
        const url = generateApiUrl({
            endpoint: `/absences/${absenceId}`,
        })

        const response = await authorizedFetch(url, {
            method: 'DELETE',
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        if (! response.ok) {
            const doc: JapiDocument = await response.json()

            return {
                status: response.status,
                type: FetchResultType.Error,
                error: doc.errors[0].detail || trans('errors.unknownError'),
            }
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: {},
        }
    } catch (error) {
        console.error('[deleteAbsenceApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}
